import React, {useState, useEffect} from 'react';
import {
    PieChart,
    Pie,
    Cell,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer
} from 'recharts';

const ExpenseCalculator = () => {
    // Initialize with default data
    const defaultExpenses = [
        {id: 1, name: 'Loan EMI', amount: 22755, category: 'EMI', color: '#FF6B6B'},
        {id: 2, name: 'Ed. Loan EMI', amount: 7430, category: 'EMI', color: '#4ECDC4'},
        {id: 3, name: 'AC EMI', amount: 11409, category: 'EMI', color: '#45B7D1'},
        {id: 4, name: 'ONECARD', amount: 2343, category: 'Credit Card', color: '#96CEB4'},
        {id: 5, name: 'Amazon Card', amount: 1733, category: 'Credit Card', color: '#a7b3ff'},
        {id: 6, name: 'HDFC 7796', amount: 7221, category: 'Credit Card', color: '#DDA0DD'},
        {id: 7, name: 'HDFC 0572', amount: 1689, category: 'Credit Card', color: '#98D8C8'},
    ];

    // add default expenses to localStorage if not already present
    // useEffect(() => {
    //     const savedExpenses = localStorage.getItem('expenses');
    //     if (!savedExpenses) {
    //         saveExpensesToStorage(defaultExpenses)
    //     }
    // }, []);

    useEffect(() => {
        loadExpensesFromStorage();
    }, []);


    // State management
    const [income, setIncome] = useState(100000);
    const [expenses, setExpenses] = useState(defaultExpenses);
    const [showAddForm, setShowAddForm] = useState(false);
    const [editingExpense, setEditingExpense] = useState(null);
    const [formData, setFormData] = useState({
        name: '',
        amount: '',
        category: 'EMI',
        color: '#FF6B6B'
    });

    const categories = ['EMI', 'Credit Card', 'Utility', 'Food', 'Transport', 'Entertainment', 'Other'];
    const colorOptions = ['#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#a7b3ff', '#DDA0DD', '#98D8C8', '#FFB347', '#87CEEB', '#DEB887'];

    // Load data from memory on component mount
    useEffect(() => {
        const savedData = window.expenseData || {};
        if (savedData.income) {
            setIncome(savedData.income);
        }
        if (savedData.expenses && savedData.expenses.length > 0) {
            setExpenses(savedData.expenses);
        }
    }, []);

    // Save data to memory whenever income or expenses change
    useEffect(() => {
        window.expenseData = {
            income,
            expenses
        };
    }, [income, expenses]);

    const totalExpenses = expenses.reduce((sum, expense) => sum + expense.amount, 0);
    const savings = income - totalExpenses;
    const savingsRate = ((savings / income) * 100).toFixed(1);
    const expenseRatio = ((totalExpenses / income) * 100).toFixed(1);

    const categoryTotals = expenses.reduce((acc, expense) => {
        acc[expense.category] = (acc[expense.category] || 0) + expense.amount;
        return acc;
    }, {});

    const categoryData = Object.entries(categoryTotals).map(([category, amount]) => ({
        name: category,
        amount,
        percentage: ((amount / totalExpenses) * 100).toFixed(1)
    }));

    // Income vs Expense breakdown for pie chart
    const incomeExpenseData = [
        {name: 'Savings', amount: savings, color: '#4CAF50'},
        {name: 'EMI Payments', amount: categoryTotals['EMI'] || 0, color: '#FF6B6B'},
        {name: 'Credit Card Bills', amount: categoryTotals['Credit Card'] || 0, color: '#FF9800'},
        {
            name: 'Other Expenses',
            amount: totalExpenses - (categoryTotals['EMI'] || 0) - (categoryTotals['Credit Card'] || 0),
            color: '#9C27B0'
        }
    ].filter(item => item.amount > 0);

    // Monthly comparison data for bar chart
    const monthlyData = [
        {
            name: 'Monthly Finance',
            Income: income,
            Expenses: totalExpenses,
            Savings: savings
        }
    ];

    const formatCurrency = (amount) => {
        return `₹${amount.toLocaleString('en-IN')}`;
    };

    const getSavingsStatus = () => {
        if (savings < 0) return {status: 'Deficit', color: '#f44336', icon: '⚠️'};
        if (savingsRate < 10) return {status: 'Low Savings', color: '#ff9800', icon: '⚡'};
        if (savingsRate < 20) return {status: 'Good Savings', color: '#2196f3', icon: '📈'};
        return {status: 'Excellent Savings', color: '#4caf50', icon: '🎯'};
    };

    const savingsStatus = getSavingsStatus();

    // Form handling functions

    const handleCancelForm = () => {
        setFormData({name: '', amount: '', category: 'EMI', color: '#FF6B6B'});
        setShowAddForm(false);
        setEditingExpense(null);
    };


    // Function to save expenses to localStorage
    const saveExpensesToStorage = (expenses) => {
        try {
            localStorage.setItem('expenses', JSON.stringify(expenses));
        } catch (error) {
            console.error('Error saving expenses to localStorage:', error);
        }
    };

// Function to get expenses from localStorage
    const getExpensesFromStorage = () => {
        try {
            const savedExpenses = localStorage.getItem('expenses');
            return savedExpenses ? JSON.parse(savedExpenses) : [];
        } catch (error) {
            console.error('Error loading expenses from localStorage:', error);
            return [];
        }
    };

// Function to clear all expenses from localStorage
    const clearExpensesFromStorage = () => {
        try {
            localStorage.removeItem('expenses');
        } catch (error) {
            console.error('Error clearing expenses from localStorage:', error);
        }
    };

    const handleAddExpense = () => {
        if (!formData.name || !formData.amount) return;

        const newExpense = {
            id: Date.now(),
            name: formData.name,
            amount: parseFloat(formData.amount),
            category: formData.category,
            color: formData.color
        };

        const updatedExpenses = [...expenses, newExpense];
        setExpenses(updatedExpenses);
        saveExpensesToStorage(updatedExpenses); // Save to localStorage

        setFormData({name: '', amount: '', category: 'EMI', color: '#FF6B6B'});
        setShowAddForm(false);
    };

    const handleEditExpense = (expense) => {
        setEditingExpense(expense);
        setFormData({
            name: expense.name,
            amount: expense.amount.toString(),
            category: expense.category,
            color: expense.color
        });
        setShowAddForm(true);
    };

    const handleUpdateExpense = () => {
        if (!formData.name || !formData.amount) return;

        const updatedExpenses = expenses.map(expense =>
            expense.id === editingExpense.id
                ? {
                    ...expense,
                    name: formData.name,
                    amount: parseFloat(formData.amount),
                    category: formData.category,
                    color: formData.color
                }
                : expense
        );

        setExpenses(updatedExpenses);
        saveExpensesToStorage(updatedExpenses); // Save to localStorage

        setFormData({name: '', amount: '', category: 'EMI', color: '#FF6B6B'});
        setShowAddForm(false);
        setEditingExpense(null);
    };

    const handleDeleteExpense = (id) => {
        if (window.confirm('Are you sure you want to delete this expense?')) {
            const updatedExpenses = expenses.filter(expense => expense.id !== id);
            setExpenses(updatedExpenses);
            saveExpensesToStorage(updatedExpenses); // Save to localStorage
        }
    };

    const loadExpensesFromStorage = () => {
        const savedExpenses = getExpensesFromStorage();
        setExpenses(savedExpenses);
    };


    const styles = {
        container: {
            padding: '24px',
            maxWidth: '1200px',
            margin: '0 auto',
            background: 'linear-gradient(135deg, #e3f2fd 0%, #e8eaf6 100%)',
            minHeight: '100vh',
            fontFamily: 'Arial, sans-serif'
        },
        card: {
            backgroundColor: 'white',
            borderRadius: '12px',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
            padding: '24px',
            marginBottom: '24px'
        },
        title: {
            fontSize: '2rem',
            fontWeight: 'bold',
            textAlign: 'center',
            color: '#333',
            marginBottom: '8px'
        },
        incomeInput: {
            width: '200px',
            padding: '8px 12px',
            fontSize: '1.1rem',
            border: '2px solid #ddd',
            borderRadius: '8px',
            textAlign: 'center',
            margin: '0 auto',
            display: 'block',
            marginBottom: '16px'
        },
        addButton: {
            backgroundColor: '#4CAF50',
            color: 'white',
            border: 'none',
            padding: '12px 24px',
            borderRadius: '8px',
            fontSize: '1rem',
            fontWeight: '600',
            cursor: 'pointer',
            marginBottom: '16px',
            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
        },
        formContainer: {
            backgroundColor: '#f8f9fa',
            border: '2px solid #4CAF50',
            borderRadius: '12px',
            padding: '20px',
            marginBottom: '24px'
        },
        formGrid: {
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '16px',
            marginBottom: '16px'
        },
        formGroup: {
            display: 'flex',
            flexDirection: 'column'
        },
        formLabel: {
            fontSize: '0.9rem',
            fontWeight: '600',
            color: '#333',
            marginBottom: '4px'
        },
        formInput: {
            padding: '8px 12px',
            border: '1px solid #ddd',
            borderRadius: '6px',
            fontSize: '1rem'
        },
        formSelect: {
            padding: '8px 12px',
            border: '1px solid #ddd',
            borderRadius: '6px',
            fontSize: '1rem',
            backgroundColor: 'white'
        },
        colorGrid: {
            display: 'grid',
            gridTemplateColumns: 'repeat(10, 1fr)',
            gap: '8px',
            marginTop: '8px'
        },
        colorOption: {
            width: '30px',
            height: '30px',
            borderRadius: '50%',
            border: '2px solid transparent',
            cursor: 'pointer'
        },
        colorOptionSelected: {
            border: '2px solid #333'
        },
        formButtons: {
            display: 'flex',
            gap: '12px',
            justifyContent: 'flex-end'
        },
        saveButton: {
            backgroundColor: '#4CAF50',
            color: 'white',
            border: 'none',
            padding: '10px 20px',
            borderRadius: '6px',
            fontSize: '1rem',
            fontWeight: '600',
            cursor: 'pointer'
        },
        cancelButton: {
            backgroundColor: '#f44336',
            color: 'white',
            border: 'none',
            padding: '10px 20px',
            borderRadius: '6px',
            fontSize: '1rem',
            fontWeight: '600',
            cursor: 'pointer'
        },
        kpiGrid: {
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '16px',
            marginBottom: '24px'
        },
        kpiCard: {
            backgroundColor: 'white',
            borderRadius: '12px',
            boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
            padding: '20px',
            textAlign: 'center',
            position: 'relative',
            overflow: 'hidden'
        },
        kpiAmount: {
            fontSize: '1.8rem',
            fontWeight: 'bold',
            marginBottom: '4px'
        },
        kpiLabel: {
            fontSize: '0.9rem',
            color: '#666',
            textTransform: 'uppercase',
            letterSpacing: '0.5px'
        },
        kpiPercentage: {
            fontSize: '0.8rem',
            fontWeight: '600',
            marginTop: '4px'
        },
        gridContainer: {
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
            gap: '24px',
            marginBottom: '24px'
        },
        categoryGrid: {
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
            gap: '16px',
            marginBottom: '24px'
        },
        categoryCard: {
            backgroundColor: 'white',
            borderRadius: '8px',
            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
            padding: '16px',
            borderLeft: '4px solid #3f51b5'
        },
        categoryTitle: {
            fontWeight: '600',
            color: '#333',
            marginBottom: '4px'
        },
        categoryAmount: {
            fontSize: '1.5rem',
            fontWeight: 'bold',
            color: '#3f51b5',
            marginBottom: '4px'
        },
        categoryPercentage: {
            fontSize: '0.875rem',
            color: '#666'
        },
        table: {
            width: '100%',
            borderCollapse: 'collapse',
            marginTop: '16px'
        },
        tableHeader: {
            backgroundColor: '#f5f5f5',
            padding: '12px 16px',
            textAlign: 'left',
            fontWeight: '600',
            color: '#333',
            borderBottom: '1px solid #ddd'
        },
        tableCell: {
            padding: '12px 16px',
            borderBottom: '1px solid #eee'
        },
        categoryBadge: {
            padding: '4px 8px',
            borderRadius: '12px',
            fontSize: '0.75rem',
            fontWeight: '500',
            backgroundColor: '#e3f2fd',
            color: '#1976d2'
        },
        actionButton: {
            padding: '4px 8px',
            margin: '0 2px',
            border: 'none',
            borderRadius: '4px',
            fontSize: '0.8rem',
            cursor: 'pointer',
            fontWeight: '500'
        },
        editButton: {
            backgroundColor: '#2196F3',
            color: 'white'
        },
        deleteButton: {
            backgroundColor: '#f44336',
            color: 'white'
        },
        savingsAlert: {
            padding: '16px',
            borderRadius: '8px',
            marginBottom: '24px',
            textAlign: 'center',
            fontSize: '1.1rem',
            fontWeight: '600'
        }
    };

    return (
        <div style={styles.container}>
            <div style={styles.card}>
                <h1 style={styles.title}>Monthly Financial Analysis</h1>
                <div style={{textAlign: 'center', marginBottom: '16px'}}>
                    <label style={{display: 'block', marginBottom: '8px', fontWeight: '600', color: '#333'}}>
                        Monthly Income (₹)
                    </label>
                    <input
                        type="number"
                        value={income}
                        onChange={(e) => setIncome(Number(e.target.value))}
                        style={styles.incomeInput}
                    />
                </div>

                <div style={{textAlign: 'center'}}>
                    <button
                        onClick={() => setShowAddForm(!showAddForm)}
                        style={styles.addButton}
                    >
                        {showAddForm ? 'Cancel' : '+ Add New Expense'}
                    </button>
                </div>
            </div>

            {showAddForm && (
                <div style={styles.formContainer}>
                    <h3 style={{marginBottom: '16px', color: '#333'}}>
                        {editingExpense ? 'Edit Expense' : 'Add New Expense'}
                    </h3>

                    <div style={styles.formGrid}>
                        <div style={styles.formGroup}>
                            <label style={styles.formLabel}>Expense Name</label>
                            <input
                                type="text"
                                value={formData.name}
                                onChange={(e) => setFormData({...formData, name: e.target.value})}
                                style={styles.formInput}
                                placeholder="Enter expense name"
                            />
                        </div>

                        <div style={styles.formGroup}>
                            <label style={styles.formLabel}>Amount (₹)</label>
                            <input
                                type="number"
                                value={formData.amount}
                                onChange={(e) => setFormData({...formData, amount: e.target.value})}
                                style={styles.formInput}
                                placeholder="Enter amount"
                            />
                        </div>

                        <div style={styles.formGroup}>
                            <label style={styles.formLabel}>Category</label>
                            <select
                                value={formData.category}
                                onChange={(e) => setFormData({...formData, category: e.target.value})}
                                style={styles.formSelect}
                            >
                                {categories.map(category => (
                                    <option key={category} value={category}>{category}</option>
                                ))}
                            </select>
                        </div>

                        <div style={styles.formGroup}>
                            <label style={styles.formLabel}>Color</label>
                            <div style={styles.colorGrid}>
                                {colorOptions.map(color => (
                                    <div
                                        key={color}
                                        onClick={() => setFormData({...formData, color})}
                                        style={{
                                            ...styles.colorOption,
                                            backgroundColor: color,
                                            ...(formData.color === color ? styles.colorOptionSelected : {})
                                        }}
                                    />
                                ))}
                            </div>
                        </div>
                    </div>

                    <div style={styles.formButtons}>
                        <button onClick={handleCancelForm} style={styles.cancelButton}>
                            Cancel
                        </button>
                        <button
                            onClick={editingExpense ? handleUpdateExpense : handleAddExpense}
                            style={styles.saveButton}
                        >
                            {editingExpense ? 'Update Expense' : 'Add Expense'}
                        </button>
                    </div>
                </div>
            )}

            <div style={{
                ...styles.savingsAlert,
                backgroundColor: savingsStatus.color + '20',
                color: savingsStatus.color,
                border: `2px solid ${savingsStatus.color}`
            }}>
                {savingsStatus.icon} {savingsStatus.status}: {savings >= 0 ? `Saving ${formatCurrency(savings)} (${savingsRate}%)` : `Deficit of ${formatCurrency(Math.abs(savings))}`}
            </div>

            <div style={styles.kpiGrid}>
                <div style={{...styles.kpiCard, borderLeft: '4px solid #4CAF50'}}>
                    <div style={{...styles.kpiAmount, color: '#4CAF50'}}>{formatCurrency(income)}</div>
                    <div style={styles.kpiLabel}>Monthly Income</div>
                </div>

                <div style={{...styles.kpiCard, borderLeft: '4px solid #f44336'}}>
                    <div style={{...styles.kpiAmount, color: '#f44336'}}>{formatCurrency(totalExpenses)}</div>
                    <div style={styles.kpiLabel}>Total Expenses</div>
                    <div style={{...styles.kpiPercentage, color: '#f44336'}}>{expenseRatio}% of income</div>
                </div>

                <div style={{...styles.kpiCard, borderLeft: `4px solid ${savingsStatus.color}`}}>
                    <div style={{...styles.kpiAmount, color: savingsStatus.color}}>{formatCurrency(savings)}</div>
                    <div style={styles.kpiLabel}>Monthly Savings</div>
                    <div style={{...styles.kpiPercentage, color: savingsStatus.color}}>{savingsRate}% savings rate</div>
                </div>
            </div>

            <div style={styles.gridContainer}>
                <div style={styles.card}>
                    <h2 style={{fontSize: '1.25rem', fontWeight: '600', color: '#333', marginBottom: '16px'}}>
                        Income Distribution
                    </h2>
                    <ResponsiveContainer width="100%" height={300}>
                        <PieChart>
                            <Pie
                                data={incomeExpenseData}
                                cx="50%"
                                cy="50%"
                                labelLine={false}
                                label={({name, amount}) => `${name}: ${formatCurrency(amount)}`}
                                outerRadius={80}
                                fill="#8884d8"
                                dataKey="amount"
                            >
                                {incomeExpenseData.map((entry, index) => (
                                    <Cell key={`cell-${index}`} fill={entry.color}/>
                                ))}
                            </Pie>
                            <Tooltip formatter={(value) => formatCurrency(value)}/>
                        </PieChart>
                    </ResponsiveContainer>
                </div>

                <div style={styles.card}>
                    <h2 style={{fontSize: '1.25rem', fontWeight: '600', color: '#333', marginBottom: '16px'}}>
                        Financial Overview
                    </h2>
                    <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={monthlyData}>
                            <CartesianGrid strokeDasharray="3 3"/>
                            <XAxis dataKey="name"/>
                            <YAxis tickFormatter={(value) => `₹${(value / 1000).toFixed(0)}k`}/>
                            <Tooltip formatter={(value) => formatCurrency(value)}/>
                            <Bar dataKey="Income" fill="#4CAF50" name="Income"/>
                            <Bar dataKey="Expenses" fill="#f44336" name="Expenses"/>
                            <Bar dataKey="Savings" fill="#2196F3" name="Savings"/>
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            </div>

            <div style={styles.gridContainer}>
                <div style={styles.card}>
                    <h2 style={{fontSize: '1.25rem', fontWeight: '600', color: '#333', marginBottom: '16px'}}>Expense
                        Breakdown</h2>
                    <ResponsiveContainer width="100%" height={300}>
                        <PieChart>
                            <Pie
                                data={expenses}
                                cx="50%"
                                cy="50%"
                                labelLine={false}
                                label={({name, amount}) => `${name}: ${formatCurrency(amount)}`}
                                outerRadius={80}
                                fill="#8884d8"
                                dataKey="amount"
                            >
                                {expenses.map((entry, index) => (
                                    <Cell key={`cell-${index}`} fill={entry.color}/>
                                ))}
                            </Pie>
                            <Tooltip formatter={(value) => formatCurrency(value)}/>
                        </PieChart>
                    </ResponsiveContainer>
                </div>

                <div style={styles.card}>
                    <h2 style={{fontSize: '1.25rem', fontWeight: '600', color: '#333', marginBottom: '16px'}}>Category
                        Totals</h2>
                    <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={categoryData}>
                            <CartesianGrid strokeDasharray="3 3"/>
                            <XAxis dataKey="name"/>
                            <YAxis tickFormatter={(value) => `₹${(value / 1000).toFixed(0)}k`}/>
                            <Tooltip formatter={(value) => formatCurrency(value)}/>
                            <Bar dataKey="amount" fill="#4F46E5"/>
                        </BarChart>
                    </ResponsiveContainer>
                </div>
            </div>

            <div style={styles.categoryGrid}>
                {categoryData.map((category, index) => (
                    <div key={index} style={styles.categoryCard}>
                        <h3 style={styles.categoryTitle}>{category.name}</h3>
                        <div style={styles.categoryAmount}>{formatCurrency(category.amount)}</div>
                        <div style={styles.categoryPercentage}>{category.percentage}% of expenses</div>
                        <div style={{fontSize: '0.75rem', color: '#888', marginTop: '4px'}}>
                            {((category.amount / income) * 100).toFixed(1)}% of income
                        </div>
                    </div>
                ))}
            </div>

            <div style={styles.card}>
                <h2 style={{fontSize: '1.25rem', fontWeight: '600', color: '#333', marginBottom: '16px'}}>Detailed
                    Breakdown</h2>
                <table style={styles.table}>
                    <thead>
                    <tr>
                        <th style={{...styles.tableHeader, textAlign: 'left'}}>Expense</th>
                        <th style={{...styles.tableHeader, textAlign: 'left'}}>Category</th>
                        <th style={{...styles.tableHeader, textAlign: 'right'}}>Amount</th>
                        <th style={{...styles.tableHeader, textAlign: 'right'}}>% of Expenses</th>
                        <th style={{...styles.tableHeader, textAlign: 'right'}}>% of Income</th>
                        <th style={{...styles.tableHeader, textAlign: 'center'}}>Actions</th>
                    </tr>
                    </thead>
                    <tbody>
                    {expenses.map((expense, index) => (
                        <tr key={expense.id}>
                            <td style={{...styles.tableCell, fontWeight: '500', color: '#333'}}>{expense.name}</td>
                            <td style={styles.tableCell}>
                                <span style={styles.categoryBadge}>{expense.category}</span>
                            </td>
                            <td style={{...styles.tableCell, textAlign: 'right', fontWeight: '600', color: '#333'}}>
                                {formatCurrency(expense.amount)}
                            </td>
                            <td style={{...styles.tableCell, textAlign: 'right', color: '#666'}}>
                                {((expense.amount / totalExpenses) * 100).toFixed(1)}%
                            </td>
                            <td style={{...styles.tableCell, textAlign: 'right', color: '#666'}}>
                                {((expense.amount / income) * 100).toFixed(1)}%
                            </td>
                            <td style={{...styles.tableCell, textAlign: 'center'}}>

                                <button
                                    onClick={() => handleDeleteExpense(expense.id)}
                                    style={{...styles.actionButton, ...styles.deleteButton}}
                                >
                                    Delete
                                </button>
                            </td>
                        </tr>
                    ))}
                    </tbody>
                </table>
            </div>

            <div style={{
                background: `linear-gradient(135deg, ${savingsStatus.color} 0%, ${savingsStatus.color}dd 100%)`,
                color: 'white',
                borderRadius: '12px',
                boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
                padding: '24px'
            }}>
                <h2 style={{fontSize: '1.25rem', fontWeight: '600', marginBottom: '16px'}}>Financial Summary</h2>
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                    gap: '16px'
                }}>
                    <div style={{textAlign: 'center'}}>
                        <div style={{fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '4px'}}>
                            {formatCurrency(categoryTotals['EMI'] || 0)}
                        </div>
                        <div style={{fontSize: '0.875rem', opacity: 0.9}}>Total EMIs</div>
                        <div style={{fontSize: '0.75rem', opacity: 0.8}}>
                            {(((categoryTotals['EMI'] || 0) / income) * 100).toFixed(1)}% of income
                        </div>
                    </div>
                    <div style={{textAlign: 'center'}}>
                        <div style={{fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '4px'}}>
                            {formatCurrency(categoryTotals['Credit Card'] || 0)}
                        </div>
                        <div style={{fontSize: '0.875rem', opacity: 0.9}}>Credit Cards</div>
                        <div style={{fontSize: '0.75rem', opacity: 0.8}}>
                            {(((categoryTotals['Credit Card'] || 0) / income) * 100).toFixed(1)}% of income
                        </div>
                    </div>
                    <div style={{textAlign: 'center'}}>
                        <div style={{fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '4px'}}>
                            {formatCurrency(savings)}
                        </div>
                        <div style={{fontSize: '0.875rem', opacity: 0.9}}>Monthly Savings</div>
                        <div style={{fontSize: '0.75rem', opacity: 0.8}}>
                            {savingsRate}% savings rate
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ExpenseCalculator;
